import { Component, effect, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { map, merge, switchMap, combineLatest, EMPTY, filter } from 'rxjs';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { environment } from '../environments/environment';
import { EnvironmentTier } from '../environments/environment.model';

import { EaLoader } from './components/ea-loader/ea-loader';
import { EaSnackbar } from './components/ea-snackbar/ea-snackbar';
import { HideAppLoader, ShowAppLoader } from './app.actions';
import { InitializeEventTreeInstance, StartEventTreeService, SetEventTreeError } from './states/event-tree/event-tree.actions';
import { ApplicationState } from './states/application/application.state';
import { GameState } from './states/game/game.state';
import { TeamState } from './states/team/team.state';
import { SnackbarService } from './services/snackbar/snackbar.service';
import { RosterState } from './states/roster/roster.state';
import { EventState } from './states/event/event.state';
import { actionDispatchReturnError } from './utils/operator.utils';

@Component({
    selector: 'ea-root',
    imports: [
        RouterOutlet,
        EaLoader,
        EaSnackbar
    ],
    templateUrl: './app.html',
    styleUrl: './app.scss',
})
export class App {
    private readonly _actions = inject(Actions);
    private readonly _store = inject(Store);
    private readonly _$isHomeTeam = this._store.selectSignal(TeamState.isHomeTeam);
    private readonly _$game = this._store.selectSignal(GameState.selectors.game);
    private readonly _selectedApplicationId$ = this._store.select(ApplicationState.selectedApplicationId);
    private readonly _roster$ = this._store.select(RosterState.selectors.roster);
    private readonly _vidParams$ = this._store.select(GameState.selectors.vidParams);
    private readonly _playerEventDefinitions$ = this._store.select(EventState.selectors.playerEventDefinitions);
    private readonly _selectedPeriod$ = this._store.select(GameState.selectors.selectedPeriod);

    protected readonly snackbarService = inject(SnackbarService);

    protected favicon = document.getElementById('favicon') as HTMLLinkElement;

    protected $showLoader = toSignal(merge(
        this._actions.pipe(ofActionDispatched(ShowAppLoader)).pipe(map(() => true)),
        this._actions.pipe(ofActionDispatched(HideAppLoader)).pipe(map(() => false))
    ), {
        initialValue: true
    });

    constructor(){
        // set the favicon to the appropriate one for the current environment
        if ( this.favicon && environment.tier !== EnvironmentTier.PRODUCTION ) {
            if ( environment.tier === EnvironmentTier.LOCAL ) {
                this.favicon.href = '/assets/favicon/favicon_local.ico';
            } else if ( environment.tier === EnvironmentTier.QA ) {
                this.favicon.href = '/assets/favicon/favicon_qa.ico';
            }
        }

        // Re-initialize the event tree whenever the selected application changes (Eventor, PlayerShifts, TimeTracker)
        // or when the required dependencies (roster, video parameters, event definitions) are loaded/updated,
        // using the current game without reacting to game changes.
        combineLatest([
            this._selectedApplicationId$,
            this._roster$,
            this._vidParams$,
            this._playerEventDefinitions$,
            this._selectedPeriod$
        ]).pipe(
            filter(([appId, roster, vidParams, playerEventDefinitions, selectedPeriod]) =>
                !!appId && !!roster && !!vidParams && !!playerEventDefinitions && !!selectedPeriod
            ),
            switchMap(([appId]) => {
                const game = this._$game();
                if (!appId || !game?.id) {
                    return EMPTY;
                }

                return this._store.dispatch(new InitializeEventTreeInstance(game.id, parseInt(appId, 10))).pipe(
                    actionDispatchReturnError(),
                    switchMap((err) => {
                        if (err) {
                            return this._store.dispatch(new SetEventTreeError(`Initializing events error: ${err}`));
                        }
                        return this._store.dispatch(new StartEventTreeService());
                    })
                );
            }),
            takeUntilDestroyed()
        ).subscribe();

        effect(() => {
            const token = this._$isHomeTeam() ? 'var(--ea-color-team-2)' : 'var(--ea-color-team-1)';
            document.documentElement.style.setProperty('--ea-color-selected-team', token);
        });
    }
}
